import React, { useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { BarChart3, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useVaultStore } from "@/stores/billing/vault-store";
import { useBillingStore } from "@/stores/billing/billing-store";

/**
 * UsageDashboard
 * Usage analytics showing aggregate usage, per-vault breakdown,
 * and a daily usage trend chart derived from ai_usage transactions.
 */

interface UsageDashboardProps {
  orgId?: string; // Deprecated: orgId now derived from org context header
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDateShort(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export const UsageDashboard: React.FC<UsageDashboardProps> = () => {
  const { vaults, transactions, isLoading, fetchTransactions, fetchVaults } = useVaultStore();
  const { subscription } = useBillingStore();

  useEffect(() => {
    fetchVaults();
    // Fetch a large batch of ai_usage transactions for the chart
    fetchTransactions({ limit: 500, type: "ai_usage" });
  }, [fetchVaults, fetchTransactions]);

  // Aggregate usage across all vaults
  const totalAllocated = vaults.reduce((sum, v) => sum + v.allocatedBudgetCents, 0);
  const totalUsed = vaults.reduce((sum, v) => sum + v.usedBudgetCents, 0);
  const overallPercent = totalAllocated > 0 ? (totalUsed / totalAllocated) * 100 : 0;

  // Derive daily usage from ai_usage transactions
  const dailyUsageData = useMemo(() => {
    const usageTxns = transactions.filter((tx) => tx.type === "ai_usage");
    if (usageTxns.length === 0) return [];

    const dailyMap = new Map<string, number>();
    for (const tx of usageTxns) {
      const day = tx.createdAt.split("T")[0]; // YYYY-MM-DD
      const current = dailyMap.get(day) || 0;
      dailyMap.set(day, current + Math.abs(tx.amountCents));
    }

    // Sort by date and format
    return Array.from(dailyMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, amount]) => ({
        date: formatDateShort(date),
        usage: amount / 100,
      }));
  }, [transactions]);

  if (isLoading && vaults.length === 0) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Usage Dashboard
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-16 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Aggregate Usage */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Usage Overview
          </CardTitle>
          <CardDescription>
            Aggregate AI budget usage across all vaults for the current billing period.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-baseline justify-between">
            <div>
              <span className="text-3xl font-bold text-foreground">
                {formatCents(totalUsed)}
              </span>
              <span className="ml-2 text-sm text-muted-foreground">
                of {formatCents(totalAllocated)} allocated
              </span>
            </div>
            <span
              className={cn(
                "text-lg font-semibold",
                overallPercent >= 80
                  ? "text-red-600 dark:text-red-400"
                  : overallPercent >= 60
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-green-600 dark:text-green-400",
              )}
            >
              {Math.round(overallPercent)}%
            </span>
          </div>
          <Progress
            value={Math.min(100, overallPercent)}
            className={cn(
              "h-3",
              overallPercent >= 80
                ? "[&>div]:bg-red-500"
                : overallPercent >= 60
                  ? "[&>div]:bg-yellow-500"
                  : "[&>div]:bg-green-500",
            )}
          />
          {subscription && (
            <p className="text-xs text-muted-foreground">
              Plan budget: {formatCents(subscription.planBudgetCents)} / period
            </p>
          )}
        </CardContent>
      </Card>

      {/* Usage Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Daily Usage Trend
          </CardTitle>
          <CardDescription>
            AI usage over time based on transaction history.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {dailyUsageData.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No usage data available yet.
            </p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyUsageData}>
                  <defs>
                    <linearGradient id="usageGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => `$${v.toFixed(0)}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                      fontSize: "12px",
                    }}
                    formatter={(value: number) => [`$${value.toFixed(2)}`, "Usage"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="usage"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#usageGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-Vault Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Vault Breakdown</CardTitle>
          <CardDescription>
            Usage breakdown by individual vault.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {vaults.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No vaults to display.
            </p>
          ) : (
            <div className="space-y-4">
              {vaults.map((vault) => {
                const percent = Math.min(100, Math.max(0, vault.usagePercent));
                return (
                  <div key={vault.id} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{vault.name}</span>
                        <span className="text-xs text-muted-foreground capitalize">
                          ({vault.type})
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">
                          {formatCents(vault.usedBudgetCents)} / {formatCents(vault.allocatedBudgetCents)}
                        </span>
                        <span
                          className={cn(
                            "text-xs font-semibold",
                            percent >= 80
                              ? "text-red-600 dark:text-red-400"
                              : percent >= 60
                                ? "text-yellow-600 dark:text-yellow-400"
                                : "text-green-600 dark:text-green-400",
                          )}
                        >
                          {Math.round(percent)}%
                        </span>
                      </div>
                    </div>
                    <Progress
                      value={percent}
                      className={cn(
                        "h-2",
                        percent >= 80
                          ? "[&>div]:bg-red-500"
                          : percent >= 60
                            ? "[&>div]:bg-yellow-500"
                            : "[&>div]:bg-green-500",
                      )}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
